/**
 * Every query and action the console performs.
 *
 * Reads reuse the pipeline's own mappers and pure aggregation functions, so the
 * numbers on screen are computed by the same code that writes the daily report
 * email. If the report is right, the dashboard is right.
 *
 * Writes reproduce exactly what `docs/RUNBOOK.md` tells a human to do by hand in
 * the PocketBase admin, which is the entire point of this console: the two
 * multi-field edits that are easy to get wrong become one button.
 */
import { pb } from "./pb.ts";
import {
  toDeliveryRecord,
  toDestination,
  toLeadRecord,
  type PbRecord,
} from "@pipeline/io/mappers.ts";
import { buildInternalReport, type InternalReport } from "@pipeline/core/report.ts";
import { dayBoundsUtc, localDateKey, toPocketBaseDate } from "@pipeline/core/time.ts";
import type {
  DeliveryRecord,
  Destination,
  LeadRecord,
  QualityLevel,
  RoutingStatus,
  SiteDefinition,
} from "@pipeline/types.ts";
import { SITES } from "@pipeline/sites.ts";

export const COLLECTIONS = {
  leads: "pipeline_leads",
  deliveries: "pipeline_deliveries",
  destinations: "pipeline_destinations",
  alerts: "pipeline_system_alerts",
} as const;

export const sites: readonly SiteDefinition[] = SITES;

export const TIMEZONE = "Europe/Stockholm";

export function siteName(siteKey: string): string {
  return SITES.find((site) => site.key === siteKey)?.name ?? siteKey;
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export interface TodaySnapshot {
  dateKey: string;
  report: InternalReport;
  leads: LeadRecord[];
  deliveries: DeliveryRecord[];
  destinations: Destination[];
  /** Milliseconds the oldest unfinished delivery has been waiting, if any. */
  oldestPendingMs: number | null;
}

export async function loadToday(now = new Date()): Promise<TodaySnapshot> {
  const dateKey = localDateKey(now, TIMEZONE);
  const { start, end } = dayBoundsUtc(dateKey, TIMEZONE);

  const [leadRecords, destinationRecords] = await Promise.all([
    pb.collection(COLLECTIONS.leads).getFullList({
      filter: pb.filter("submitted_at >= {:start} && submitted_at < {:end}", {
        start: toPocketBaseDate(start),
        end: toPocketBaseDate(end),
      }),
      sort: "-submitted_at",
      batch: 500,
    }),
    pb.collection(COLLECTIONS.destinations).getFullList({ sort: "key", batch: 200 }),
  ]);

  const leads = (leadRecords as unknown as PbRecord[]).map(toLeadRecord);
  const destinations = (destinationRecords as unknown as PbRecord[]).map(toDestination);
  const deliveries = await deliveriesForLeads(leads.map((lead) => lead.id));

  const report = buildInternalReport({ dateKey, leads, deliveries, destinations, sites });

  return {
    dateKey,
    report,
    leads,
    deliveries,
    destinations,
    oldestPendingMs: await oldestPendingAge(now),
  };
}

async function deliveriesForLeads(leadIds: string[]): Promise<DeliveryRecord[]> {
  if (leadIds.length === 0) return [];
  const all: DeliveryRecord[] = [];

  for (let index = 0; index < leadIds.length; index += 40) {
    const chunk = leadIds.slice(index, index + 40);
    const params: Record<string, string> = {};
    const clauses = chunk.map((id, position) => {
      params[`l${position}`] = id;
      return `lead = {:l${position}}`;
    });
    const records = await pb.collection(COLLECTIONS.deliveries).getFullList({
      filter: pb.filter(clauses.join(" || "), params),
      batch: 500,
    });
    all.push(...(records as unknown as PbRecord[]).map(toDeliveryRecord));
  }
  return all;
}

async function oldestPendingAge(now: Date): Promise<number | null> {
  const page = await pb.collection(COLLECTIONS.deliveries).getList(1, 1, {
    filter: 'status = "pending" || status = "sending"',
    sort: "created",
  });
  const first = (page.items as unknown as PbRecord[])[0];
  if (!first) return null;
  const created = new Date(String(first.created).replace(" ", "T"));
  return Number.isNaN(created.getTime()) ? null : now.getTime() - created.getTime();
}

/** Leads the quality gate withheld. These are the ones at risk of being forgotten. */
export async function loadHeldLeads(): Promise<LeadRecord[]> {
  const records = await pb.collection(COLLECTIONS.leads).getFullList({
    filter: 'routing_status = "held"',
    sort: "-submitted_at",
    batch: 200,
  });
  return (records as unknown as PbRecord[]).map(toLeadRecord);
}

/**
 * Counts for the nav badges.
 *
 * `getList(1, 1)` returns `totalItems` from the same query without shipping the
 * rows, which is what a badge actually needs. The badges refresh on a timer, so
 * downloading every held lead and every failed delivery once a minute just to
 * read `.length` was real traffic for a number.
 */
async function countMatching(collection: string, filter: string): Promise<number> {
  const page = await pb.collection(collection).getList(1, 1, { filter });
  return page.totalItems;
}

export function countHeldLeads(): Promise<number> {
  return countMatching(COLLECTIONS.leads, 'routing_status = "held"');
}

export function countProblemDeliveries(): Promise<number> {
  return countMatching(COLLECTIONS.deliveries, DELIVERY_VIEW_FILTERS.problems);
}

export interface DeliveryWithContext {
  delivery: DeliveryRecord;
  lead: LeadRecord | null;
  destination: Destination | null;
}

/** One page of results, plus what the pager needs to describe itself. */
export interface Paged<T> {
  items: T[];
  page: number;
  perPage: number;
  totalItems: number;
  totalPages: number;
}

/** Fetch leads by id, chunked so a filter string can't grow unbounded. */
async function leadsByIds(ids: string[]): Promise<Map<string, LeadRecord>> {
  const leads = new Map<string, LeadRecord>();
  const unique = [...new Set(ids)].filter(Boolean);

  for (let index = 0; index < unique.length; index += 40) {
    const chunk = unique.slice(index, index + 40);
    const params: Record<string, string> = {};
    const clauses = chunk.map((id, position) => {
      params[`i${position}`] = id;
      return `id = {:i${position}}`;
    });
    const records = await pb.collection(COLLECTIONS.leads).getFullList({
      filter: pb.filter(clauses.join(" || "), params),
      batch: 200,
    });
    for (const record of records as unknown as PbRecord[]) {
      const lead = toLeadRecord(record);
      leads.set(lead.id, lead);
    }
  }
  return leads;
}

// ---------------------------------------------------------------------------
// Deliveries
// ---------------------------------------------------------------------------

/**
 * Which slice of the outbox to show.
 *
 * `problems` stays the default everywhere. Deliveries is the action queue, and
 * a queue that also lists nine hundred successful sends is not a queue. The
 * other views exist because this underpins a partner relationship: "we never
 * received that lead" is the dispute that will eventually arrive, and the full
 * log is the evidence.
 *
 * These strings are fixed literals, never built from input, so they need no
 * escaping — unlike the lead filters below.
 */
export const DELIVERY_VIEWS = ["problems", "all", "sent", "pending"] as const;
export type DeliveryView = (typeof DELIVERY_VIEWS)[number];

const DELIVERY_VIEW_FILTERS: Record<DeliveryView, string> = {
  problems: 'status = "dead" || status = "bounced" || status = "complained"',
  all: "",
  sent: 'status = "sent" || status = "delivered"',
  pending: 'status = "pending" || status = "sending"',
};

export const DELIVERY_PAGE_SIZE = 25;

/**
 * One page of the outbox with its lead and destination attached.
 *
 * Paged rather than `getFullList`, because `all` grows with every lead ever
 * forwarded and the point of the log is that it keeps everything.
 */
export async function loadDeliveryPage(
  view: DeliveryView,
  page = 1,
  perPage = DELIVERY_PAGE_SIZE,
): Promise<Paged<DeliveryWithContext>> {
  const [result, destinationRecords] = await Promise.all([
    pb.collection(COLLECTIONS.deliveries).getList(page, perPage, {
      filter: DELIVERY_VIEW_FILTERS[view],
      // Problems sort by last change, so a fresh failure surfaces first. The
      // log views sort by creation, which is the order a dispute is argued in.
      sort: view === "problems" ? "-updated" : "-created",
    }),
    pb.collection(COLLECTIONS.destinations).getFullList({ batch: 200 }),
  ]);

  const deliveries = (result.items as unknown as PbRecord[]).map(toDeliveryRecord);
  const destinations = new Map(
    (destinationRecords as unknown as PbRecord[]).map(toDestination).map((d) => [d.id, d]),
  );
  const leads = await leadsByIds(deliveries.map((delivery) => delivery.lead));

  return {
    items: deliveries.map((delivery) => ({
      delivery,
      lead: leads.get(delivery.lead) ?? null,
      destination: destinations.get(delivery.destination) ?? null,
    })),
    page: result.page,
    perPage: result.perPage,
    totalItems: result.totalItems,
    totalPages: result.totalPages,
  };
}

// ---------------------------------------------------------------------------
// Leads (all of them, all time)
// ---------------------------------------------------------------------------

export interface LeadFilters {
  /** Free text matched against name, email and company. */
  search: string;
  /** A `SITES` key, or "" for every site. */
  siteKey: string;
  quality: QualityLevel | "";
  routingStatus: RoutingStatus | "";
}

export const EMPTY_LEAD_FILTERS: LeadFilters = {
  search: "",
  siteKey: "",
  quality: "",
  routingStatus: "",
};

export interface LeadWithDeliveries {
  lead: LeadRecord;
  deliveries: Array<{ delivery: DeliveryRecord; destination: Destination | null }>;
}

export const LEAD_PAGE_SIZE = 25;

/**
 * Build the PocketBase filter for the lead list.
 *
 * Every value goes through `pb.filter()` parameter binding. PocketBase filters
 * are a query language, not a string: concatenating a search box into one lets
 * a stray quote change the query's meaning. The SDK quotes and escapes each
 * bound value, so `o'brien` and `" || 1=1 || "` are matched as literal text.
 *
 * The clauses are joined with `&&` and each is parenthesised, so adding an
 * `||` group later cannot silently rebind the precedence of the others.
 */
export function buildLeadFilter(filters: LeadFilters): string {
  const clauses: string[] = [];

  const search = filters.search.trim();
  if (search) {
    // `~` is PocketBase's LIKE. `%` and `_` inside the term stay wildcards,
    // which is a search box behaving like a search box, not an injection.
    clauses.push(pb.filter("(name ~ {:q} || email ~ {:q} || company ~ {:q})", { q: search }));
  }
  if (filters.siteKey) {
    clauses.push(pb.filter("(site_key = {:siteKey})", { siteKey: filters.siteKey }));
  }
  if (filters.quality) {
    clauses.push(pb.filter("(quality = {:quality})", { quality: filters.quality }));
  }
  if (filters.routingStatus) {
    clauses.push(pb.filter("(routing_status = {:routing})", { routing: filters.routingStatus }));
  }

  return clauses.join(" && ");
}

/**
 * One page of every lead ever captured, newest first, with each lead's delivery
 * outcome attached.
 *
 * Deliberately `getList`, never `getFullList`: this table only grows, and the
 * whole point of the screen is that it reaches back past today.
 */
export async function loadLeadPage(
  filters: LeadFilters,
  page = 1,
  perPage = LEAD_PAGE_SIZE,
): Promise<Paged<LeadWithDeliveries>> {
  const result = await pb.collection(COLLECTIONS.leads).getList(page, perPage, {
    filter: buildLeadFilter(filters),
    sort: "-submitted_at",
  });

  const leads = (result.items as unknown as PbRecord[]).map(toLeadRecord);

  const [deliveries, destinationRecords] = await Promise.all([
    deliveriesForLeads(leads.map((lead) => lead.id)),
    pb.collection(COLLECTIONS.destinations).getFullList({ batch: 200 }),
  ]);

  const destinations = new Map(
    (destinationRecords as unknown as PbRecord[]).map(toDestination).map((d) => [d.id, d]),
  );

  const byLead = new Map<string, LeadWithDeliveries["deliveries"]>();
  for (const delivery of deliveries) {
    const list = byLead.get(delivery.lead) ?? [];
    list.push({ delivery, destination: destinations.get(delivery.destination) ?? null });
    byLead.set(delivery.lead, list);
  }

  return {
    items: leads.map((lead) => ({ lead, deliveries: byLead.get(lead.id) ?? [] })),
    page: result.page,
    perPage: result.perPage,
    totalItems: result.totalItems,
    totalPages: result.totalPages,
  };
}

export async function loadDestinations(): Promise<Destination[]> {
  const records = await pb.collection(COLLECTIONS.destinations).getFullList({
    sort: "key",
    batch: 200,
  });
  return (records as unknown as PbRecord[]).map(toDestination);
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

/**
 * Release a lead the quality gate withheld.
 *
 * Mirrors the runbook: mark it clean and send it back to `pending` so the next
 * routing pass picks it up. Two fields that must move together; getting one
 * wrong by hand either loses the lead or leaves it stuck.
 */
export async function releaseLead(leadId: string): Promise<void> {
  await pb.collection(COLLECTIONS.leads).update(leadId, {
    quality: "clean",
    routing_status: "pending",
  });
}

/** Mark a held lead as spam so it stops appearing in the review queue. */
export async function discardLead(leadId: string): Promise<void> {
  await pb.collection(COLLECTIONS.leads).update(leadId, {
    quality: "spam",
    routing_status: "skipped",
  });
}

/**
 * Retry a failed delivery.
 *
 * Three fields, per the runbook. The idempotency key is deliberately left
 * untouched: if the original send was in fact accepted by the provider, the
 * retry returns the same message id instead of emailing the partner twice.
 */
export async function retryDelivery(deliveryId: string): Promise<void> {
  await pb.collection(COLLECTIONS.deliveries).update(deliveryId, {
    status: "pending",
    attempts: 0,
    next_attempt_at: new Date().toISOString(),
    last_error: "",
  });
}

export async function updateDestination(
  id: string,
  patch: Partial<{
    active: boolean;
    emails: string[];
    ccEmails: string[];
    siteKeys: string[];
    minQuality: "clean" | "suspect";
    dailyReportEnabled: boolean;
    dailyReportEmails: string[];
    includeCsvInDaily: boolean;
    name: string;
    notes: string;
    /** `null` clears the bound. Omitting the key leaves it untouched. */
    deliverFrom: Date | null;
    deliverUntil: Date | null;
  }>,
): Promise<Destination> {
  const payload: Record<string, unknown> = {};
  if (patch.active !== undefined) payload.active = patch.active;
  if (patch.emails) payload.emails = patch.emails;
  if (patch.ccEmails) payload.cc_emails = patch.ccEmails;
  if (patch.siteKeys) payload.site_keys = patch.siteKeys;
  if (patch.minQuality) payload.min_quality = patch.minQuality;
  if (patch.dailyReportEnabled !== undefined) payload.daily_report_enabled = patch.dailyReportEnabled;
  if (patch.dailyReportEmails) payload.daily_report_emails = patch.dailyReportEmails;
  if (patch.includeCsvInDaily !== undefined) payload.include_csv_in_daily = patch.includeCsvInDaily;
  if (patch.name !== undefined) payload.name = patch.name;
  if (patch.notes !== undefined) payload.notes = patch.notes;
  // "" is how PocketBase clears a date. Must be checked with `!== undefined`,
  // because `null` is the meaningful "clear this bound" value.
  if (patch.deliverFrom !== undefined) {
    payload.deliver_from = patch.deliverFrom ? patch.deliverFrom.toISOString() : "";
  }
  if (patch.deliverUntil !== undefined) {
    payload.deliver_until = patch.deliverUntil ? patch.deliverUntil.toISOString() : "";
  }

  const record = await pb.collection(COLLECTIONS.destinations).update(id, payload);

  // PocketBase silently drops fields the collection does not have, so a
  // database that is behind on `pnpm setup:pocketbase` accepts the write,
  // stores nothing, and the console toasts success over a no-op. The response
  // echoes every real field; one we sent but did not get back must be a lie.
  const dropped = Object.keys(payload).filter((key) => !(key in record));
  if (dropped.length > 0) {
    throw new Error(
      `PocketBase ignored ${dropped.join(", ")} — the schema is behind. Run: pnpm setup:pocketbase`,
    );
  }

  return toDestination(record as unknown as PbRecord);
}

// ---------------------------------------------------------------------------
// Trend and volume
// ---------------------------------------------------------------------------

export interface TrendDay {
  dateKey: string;
  total: number;
  /** Leads that reached a partner. The rest are held, spam, or out of window. */
  forwarded: number;
}

/**
 * Leads per day for the last `days` days, oldest first, with empty days
 * included so the chart keeps a true time axis rather than compressing
 * quiet days out of existence.
 *
 * Bounded by construction — at a couple of leads a day this is tens of rows,
 * and the query is capped by the date filter regardless of how the table grows.
 */
export async function loadLeadTrend(days = 14, now = new Date()): Promise<TrendDay[]> {
  const todayKey = localDateKey(now, TIMEZONE);
  const { end } = dayBoundsUtc(todayKey, TIMEZONE);
  const firstKey = localDateKey(new Date(now.getTime() - (days - 1) * 86_400_000), TIMEZONE);
  const { start } = dayBoundsUtc(firstKey, TIMEZONE);

  const records = await pb.collection(COLLECTIONS.leads).getFullList({
    filter: pb.filter("submitted_at >= {:start} && submitted_at < {:end}", {
      start: toPocketBaseDate(start),
      end: toPocketBaseDate(end),
    }),
    sort: "submitted_at",
    batch: 500,
  });

  const buckets = new Map<string, TrendDay>();
  for (let index = 0; index < days; index += 1) {
    const key = localDateKey(new Date(now.getTime() - (days - 1 - index) * 86_400_000), TIMEZONE);
    buckets.set(key, { dateKey: key, total: 0, forwarded: 0 });
  }

  for (const record of records as unknown as PbRecord[]) {
    const lead = toLeadRecord(record);
    const key = localDateKey(new Date(lead.submittedAt), TIMEZONE);
    const bucket = buckets.get(key);
    if (!bucket) continue;
    bucket.total += 1;
    if (lead.routingStatus === "routed") bucket.forwarded += 1;
  }

  return [...buckets.values()];
}

/**
 * Total leads ever captured per site.
 *
 * One count query per site rather than fetching rows: `getList(1, 1)` returns
 * `totalItems` without transferring records, so this stays flat as the table
 * grows. Used to make "which sites feed this partner" an informed choice
 * instead of fifteen identical-looking options.
 */
export async function loadSiteLeadCounts(): Promise<Record<string, number>> {
  const entries = await Promise.all(
    SITES.map(async (site) => {
      try {
        const result = await pb
          .collection(COLLECTIONS.leads)
          .getList(1, 1, { filter: pb.filter("site_key = {:key}", { key: site.key }) });
        return [site.key, result.totalItems] as const;
      } catch {
        return [site.key, 0] as const;
      }
    }),
  );
  return Object.fromEntries(entries);
}

// ---------------------------------------------------------------------------
// Live updates
// ---------------------------------------------------------------------------

/**
 * Subscribe to lead and delivery changes.
 *
 * The console is a window onto a pipeline that moves on its own; a stale
 * dashboard would be worse than no dashboard. Returns an unsubscribe function.
 */
export function subscribeToChanges(onChange: () => void): () => void {
  const unsubscribers: Array<Promise<() => void>> = [
    pb.collection(COLLECTIONS.leads).subscribe("*", onChange),
    pb.collection(COLLECTIONS.deliveries).subscribe("*", onChange),
  ];

  return () => {
    for (const pending of unsubscribers) {
      void pending.then((unsubscribe) => unsubscribe()).catch(() => {});
    }
  };
}
